import ConfigAPI from '@/config/api'
import Session from '@/handler/session'
import EscposRenderer from './escposRenderer'

const DB_NAME = 'zyncro-printer'
const DB_VERSION = 1
const STORE = 'local-ticket-config'

// ── IndexedDB ─────────────────────────────────────────────────────────────────

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = e => e.target.result.createObjectStore(STORE)
    req.onsuccess = e => resolve(e.target.result)
    req.onerror = e => reject(e.target.error)
  })
}

async function idbGet(key) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(key)
    req.onsuccess = e => resolve(e.target.result)
    req.onerror = e => reject(e.target.error)
  })
}

async function idbSet(key, value) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    const req = tx.objectStore(STORE).put(value, key)
    req.onsuccess = () => resolve()
    req.onerror = e => reject(e.target.error)
  })
}

// ── Cache warming ─────────────────────────────────────────────────────────────

const PRINTER_KEY = '__printer__'

async function warmCache(reference) {
  try {
    const url = ConfigAPI.baseURL + 'reports/local-ticket-cache' + Session.getToken() + '&reference=' + encodeURIComponent(reference)
    const session = Session.getSession()
    const headers = session.auth && session.auth.token
      ? { Authorization: 'Bearer ' + session.auth.token }
      : {}
    const res = await fetch(url, { headers })
    if (!res.ok) return false
    const payload = await res.json()
    if (!payload.template || typeof payload.template.content !== 'string') return false
    await idbSet(reference, { ...payload, cachedAt: Date.now() })

    // La config de impresora es por usuario (no por reporte); se guarda aparte
    // para que quede disponible offline sin depender de qué referencia se cacheó.
    if (payload.printer) {
      await idbSet(PRINTER_KEY, { ...payload.printer, cachedAt: Date.now() })
    }
    return true
  } catch (_) {
    // sin conexión — se usa el cache existente
    return false
  }
}

// Config de impresora cacheada (modo local/external + URL externa). Se usa
// como fallback offline cuando no se puede consultar staff/thermal-printer.
async function getPrinterConfig() {
  try {
    const cached = await idbGet(PRINTER_KEY)
    return {
      thermal_printer: (cached && cached.thermal_printer) || '',
      thermal_printer_mode: (cached && cached.thermal_printer_mode) || 'local',
      thermal_printer_external_url: (cached && cached.thermal_printer_external_url) || '',
    }
  } catch (_) {
    return { thermal_printer: '', thermal_printer_mode: 'local', thermal_printer_external_url: '' }
  }
}

// ── Interpolación offline; los comandos viven en escposRenderer.js ───────────

function toSnakeCase(str) {
  return str.charAt(0).toLowerCase() + str.slice(1).replace(/[A-Z]+/g, m => '_' + m.toLowerCase())
}

function resolvePath(obj, path) {
  const keys = path.trim().split('.')
  let val = obj
  for (const key of keys) {
    if (val == null) return undefined
    val = val[key] !== undefined ? val[key] : val[toSnakeCase(key)]
  }
  return val
}

function applyFormat(value, fmt) {
  if (value === undefined || value === null) return ''
  if (!fmt) return String(value)
  if (fmt === 'date') {
    const d = new Date(value)
    if (isNaN(d)) return String(value)
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }
  if (fmt === 'currency') {
    return '$ ' + Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  if (fmt === 'decimal') {
    return Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  if (fmt === 'int') return Math.round(Number(value)).toLocaleString('es-AR')
  if (fmt === 'pointSales') return String(value).padStart(4, '0')
  if (fmt === 'numberInvoice') return String(value).padStart(8, '0')
  if (fmt === 'si-no') return value ? 'SI' : 'NO'
  const wrap = /^wrap\[(\d+)\]$/.exec(fmt)
  if (wrap) {
    const width = Number(wrap[1])
    const words = String(value).split(/\s+/)
    const lines = []
    let line = ''
    words.forEach(word => {
      let remaining = word
      while (remaining.length > width) {
        if (line) {
          lines.push(line)
          line = ''
        }
        lines.push(remaining.slice(0, width))
        remaining = remaining.slice(width)
      }
      if (line && (line.length + remaining.length + 1) > width) {
        lines.push(line)
        line = remaining
      } else {
        line += (line ? ' ' : '') + remaining
      }
    })
    if (line) lines.push(line)
    return lines.join('\n')
  }
  return String(value)
}

function substituteVars(content, data) {
  return content.replace(/{{([^}]+)}}/g, (match, expr) => {
    if (expr.includes('arrayStart') || expr.includes('arrayEnd')) return match
    const parts = expr.split('|')
    const path = parts[0]
    const fmt = parts[1] ? parts[1].trim() : null
    const value = resolvePath(data, path)
    if (value === undefined || value === null) return ''
    return applyFormat(value, fmt)
  })
}

function renderArrayBlocks(content, data) {
  let result = content
  const startRe = /{{([\w.]+)\|arrayStart}}/g
  let match
  // eslint-disable-next-line no-cond-assign
  while ((match = startRe.exec(result)) !== null) {
    const key = match[1]
    const startMarker = match[0]
    const endMarker = '{{' + key + '|arrayEnd}}'
    const startPos = result.indexOf(startMarker) + startMarker.length
    const endPos = result.indexOf(endMarker)
    if (endPos === -1) continue
    const block = result.substring(startPos, endPos)
    let arr = resolvePath(data, key)
    if (!Array.isArray(arr)) arr = []
    const rendered = arr.map(item => substituteVars(block, item)).join('')
    result = result.slice(0, result.indexOf(startMarker)) + rendered + result.slice(endPos + endMarker.length)
    startRe.lastIndex = 0
  }
  return result
}

// ── API pública ───────────────────────────────────────────────────────────────

async function renderLocalTicket(reference, localData) {
  const cache = await idbGet(reference)
  if (!cache || !cache.template || !cache.template.content) {
    throw new Error('Sin cache de ticket para "' + reference + '". Conectate al menos una vez.')
  }

  const data = { ...localData, business: cache.business || {} }
  const tmpl = cache.template
  let content = tmpl.content
  content = renderArrayBlocks(content, data)
  content = substituteVars(content, data)
  content = EscposRenderer.renderCommands(content, tmpl)

  return content
}

// Resuelve el ticket local respetando el modo de impresión del staff
// (cacheado por warmCache, disponible sin conexión):
// - local: arma el ESC/POS ya renderizado, como siempre (renderLocalTicket).
// - external: NO renderiza — devuelve el JSON crudo (localData + business) para
//   que lo procese el servicio externo, igual que en el flujo online.
async function resolveLocalTicket(reference, localData) {
  const printer = await getPrinterConfig()

  if (printer.thermal_printer_mode === 'external') {
    const cache = await idbGet(reference)
    const payload = { ...localData, business: (cache && cache.business) || {} }

    return {
      mode: 'external',
      payload,
      endpoint: printer.thermal_printer_external_url,
    }
  }

  const content = await renderLocalTicket(reference, localData)

  return {
    mode: 'local',
    content,
  }
}

export default { warmCache, renderLocalTicket, resolveLocalTicket, getPrinterConfig }
